import React, { useLayoutEffect, useMemo, useState } from "react";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { lazyLoad } from "utils/misc.tsx";
import {
  ACCOUNT_EXTRA_DETAILS_PATH,
  BACKTESTING_EXTRA_DETAILS_PATH,
  BOT_EXTRA_DETAILS_PATH,
  COIN_EXTRA_DETAILS_PATH,
  GROUP_CONFIGURATION_PATH,
  MARKETPLACE_GROUP_PATH,
  PAGE_SPINNER_MARGIN_FROM_TOP,
  QUICK_ONBOARDING_PATH,
  SIGNAL_EXTRA_DETAILS_PATH,
  TRADE_EXTRA_DETAILS_PATH,
} from "common/constants.ts";
import useRoutesMatch from "utils/hooks/useRoutesMatch.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { useTheme } from "styled-components";

const TradeDrawer = lazyLoad(
  () => import("features/trades/TradeDrawer/TradeDrawer")
);
const AssetDrawer = lazyLoad(
  () => import("features/portfolio/CoinDrawer/AssetDrawer")
);
const AccountDrawer = lazyLoad(
  () => import("features/portfolio/AccountDrawer/AccountDrawer")
);
const SignalDrawer = lazyLoad(
  () => import("features/signals/SignalDrawer/SignalDrawer")
);
const BotDrawer = lazyLoad(() => import("features/bots/BotDrawer/BotDrawer"));
const GroupDrawer = lazyLoad(
  () => import("features/newMarketplace/GroupExtraDetails/GroupDrawer")
);
const BacktestingDrawer = lazyLoad(
  () =>
    import(
      "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/BacktestingDrawer"
    )
);
const GroupConfigurationDrawer = lazyLoad(
  () =>
    import(
      "features/configuration/GroupConfigurationDrawer/GroupConfigurationDrawer"
    )
);
const QuickOnboarding = lazyLoad(
  () => import("features/quickOnboarding/QuickOnboarding")
);

const Drawers = () => {
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const paths = useMemo(() => Object.keys(PATH_TO_DRAWER), []);
  const drawers = useMemo(() => Object.values(PATH_TO_DRAWER), []);
  const [visitedIndices, setVisitedIndices] = useState<boolean[]>(
    paths.map(() => false)
  );
  const currentMatch = useRoutesMatch(paths)?.[0];

  useLayoutEffect(() => {
    if (currentMatch !== undefined && !visitedIndices[currentMatch]) {
      setVisitedIndices((prevVisited) => {
        const updated = [...prevVisited];
        updated[currentMatch] = true;
        return updated;
      });
    }
  }, [currentMatch]);

  return (
    <React.Suspense
      fallback={
        <LoadingSpinnerOverlay
          isLoading={true}
          spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
          spinnerSize="large"
          backgroundColor={theme.colors.layoutBgColor}
          zIndex={2}
        />
      }
    >
      {visitedIndices.map((shouldRender, index) => {
        const path = paths[index];
        if (
          shouldRender &&
          (path !== QUICK_ONBOARDING_PATH || !isBottomNavigator)
        ) {
          return drawers[index];
        }
        return null;
      })}
    </React.Suspense>
  );
};

export default Drawers;

const PATH_TO_DRAWER = {
  [COIN_EXTRA_DETAILS_PATH]: <AssetDrawer key="assetDrawer" />,
  [BOT_EXTRA_DETAILS_PATH]: <BotDrawer key="botDrawer" />,
  [ACCOUNT_EXTRA_DETAILS_PATH]: <AccountDrawer key="accountDrawer" />,
  [GROUP_CONFIGURATION_PATH]: (
    <GroupConfigurationDrawer key="groupConfigurationDrawer" />
  ),
  [SIGNAL_EXTRA_DETAILS_PATH]: <SignalDrawer key="signalDrawer" />,
  [MARKETPLACE_GROUP_PATH]: <GroupDrawer key="groupDrawer" />,
  [BACKTESTING_EXTRA_DETAILS_PATH]: (
    <BacktestingDrawer key="backtestingDrawer" />
  ),
  [TRADE_EXTRA_DETAILS_PATH]: <TradeDrawer key="tradeDrawer" />,
  [QUICK_ONBOARDING_PATH]: <QuickOnboarding />,
};
